// Data processing utilities for PostureTrendChart

export const processChartData = (
  postureHeatmapCoverageTrendData,
  timeRange,
  heatmapDisplayMode,
  primaryColor
) => {
  if (!postureHeatmapCoverageTrendData) {
    return {
      data: [
        {
          id: "Your Posture",
          data: [],
          color: primaryColor,
        },
      ],
      metadata: {
        hasInsufficientData: true,
      },
    };
  }

  const timePointData = postureHeatmapCoverageTrendData[timeRange];
  if (!timePointData || Object.keys(timePointData).length === 0) {
    return {
      data: [
        {
          id: "Your Posture",
          data: [],
          color: primaryColor,
        },
      ],
      metadata: {
        hasInsufficientData: true,
      },
    };
  }

  const entries = Object.entries(timePointData)
    .map(([timestamp, data]) => ({
      timestamp: parseFloat(timestamp),
      coverage:
        data?.[heatmapDisplayMode]?.coverage ||
        data?.controls_based?.coverage ||
        0,
      date: new Date(parseFloat(timestamp) * 1000),
    }))
    .sort((a, b) => a.timestamp - b.timestamp);

  const isWeeklyView = timeRange === "30D";
  const aggregatedData = {};

  entries.forEach((entry) => {
    const { periodKey, periodLabel, periodDate } = getPeriodInfo(
      entry.date,
      isWeeklyView
    );

    if (!aggregatedData[periodKey]) {
      aggregatedData[periodKey] = {
        periodLabel,
        coverageValues: [],
        date: periodDate,
      };
    }
    aggregatedData[periodKey].coverageValues.push(entry.coverage);
  });

  const currentPeriodStart = getCurrentPeriodStart(isWeeklyView);
  const yourPostureData = [];
  let currentPeriodIndex = -1;
  let minValue = Infinity;
  let maxValue = -Infinity;

  const sortedPeriods = Object.values(aggregatedData).sort(
    (a, b) => a.date - b.date
  );

  const minPeriodsRequired = timeRange === "30D" ? 1 : 3;
  const hasInsufficientData = sortedPeriods.length < minPeriodsRequired;

  const firstPeriodLabel =
    sortedPeriods.length > 0 ? sortedPeriods[0].periodLabel : null;

  sortedPeriods.forEach((period, index) => {
    const avgCoverage = parseFloat(
      (
        period.coverageValues.reduce((sum, val) => sum + val, 0) /
        period.coverageValues.length
      ).toFixed(1)
    );

    yourPostureData.push({ x: period.periodLabel, y: avgCoverage });

    minValue = Math.min(minValue, avgCoverage);
    maxValue = Math.max(maxValue, avgCoverage);

    if (period.date >= currentPeriodStart && currentPeriodIndex === -1) {
      currentPeriodIndex = index;
    }
  });

  const currentPeriodLabel =
    currentPeriodIndex >= 0 ? yourPostureData[currentPeriodIndex]?.x : null;
  const lastPeriodDate = sortedPeriods[sortedPeriods.length - 1]?.date;

  const futurePeriods = generateFuturePeriods(lastPeriodDate, isWeeklyView);
  futurePeriods.forEach((futureLabel) => {
    yourPostureData.push({ x: futureLabel, y: null });
  });

  // Extend Y-axis range by 10 units above max and below min, constrained to 0-100%
  const yMin = Math.max(0, Math.floor(minValue - 10));
  const yMax = Math.min(100, Math.ceil(maxValue + 10));

  return {
    data: [
      {
        id: "Your Posture",
        data: yourPostureData,
        color: primaryColor,
      },
    ],
    metadata: {
      currentMonthLabel: currentPeriodLabel,
      currentMonthIndex: currentPeriodIndex,
      breezeDeployedMonth: firstPeriodLabel,
      yMin,
      yMax,
      lastMonthDate: lastPeriodDate,
      futureMonths: futurePeriods,
      hasInsufficientData,
    },
  };
};

const getPeriodInfo = (date, isWeeklyView) => {
  if (isWeeklyView) {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);
    startOfWeek.setHours(0, 0, 0, 0);

    const periodKey = `${startOfWeek.getFullYear()}-W${Math.floor(
      (startOfWeek.getTime() -
        new Date(startOfWeek.getFullYear(), 0, 1).getTime()) /
        (7 * 24 * 60 * 60 * 1000)
    )}`;
    const periodLabel = startOfWeek.toLocaleString("default", {
      month: "short",
      day: "numeric",
    });

    return { periodKey, periodLabel, periodDate: startOfWeek };
  } else {
    const periodKey = `${date.getFullYear()}-${date.getMonth()}`;
    const periodLabel = date.toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
    const periodDate = new Date(date.getFullYear(), date.getMonth(), 1);

    return { periodKey, periodLabel, periodDate };
  }
};

const getCurrentPeriodStart = (isWeeklyView) => {
  const currentPeriodStart = new Date();

  if (isWeeklyView) {
    const day = currentPeriodStart.getDay();
    const diff = currentPeriodStart.getDate() - day + (day === 0 ? -6 : 1);
    currentPeriodStart.setDate(diff);
  } else {
    currentPeriodStart.setDate(1);
  }

  currentPeriodStart.setHours(0, 0, 0, 0);
  return currentPeriodStart;
};

const generateFuturePeriods = (lastPeriodDate, isWeeklyView) => {
  const futurePeriods = [];

  if (lastPeriodDate) {
    for (let i = 1; i <= 2; i++) {
      const futureDate = new Date(lastPeriodDate);
      let futureLabel;

      if (isWeeklyView) {
        futureDate.setDate(futureDate.getDate() + i * 7);
        futureLabel = futureDate.toLocaleString("default", {
          month: "short",
          day: "numeric",
        });
      } else {
        futureDate.setMonth(futureDate.getMonth() + i);
        futureLabel = futureDate.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
      }

      futurePeriods.push(futureLabel);
    }
  }

  return futurePeriods;
};
